/**
 * TemplateEditor — 템플릿 편집 페이지
 *
 * 좌: Monaco 에디터 (원본 HTML / Liquid 탭), 우: 미리보기 iframe
 * 하단: 변환 이력 패널
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import Editor, { type Monaco } from '@monaco-editor/react'
import type { editor } from 'monaco-editor'
import { useApiFetch } from '../hooks/useApiFetch'
import { useMonacoModelCleanup } from '../hooks/useMonacoModelCleanup'
import { useToast } from '../components/ToastContainer'
import { apiFetch } from '../lib/api'
import type { StepwiseSession } from './template-import/useStepwiseImport'
import { emptyStepwiseState } from './template-import/stages'
import { Button } from '../ui/Button'
import { Tabs } from '../ui/Tabs'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Spinner } from '../ui/Spinner'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import { Alert } from '../ui/Alert'
import { Drawer } from '../ui/Drawer'
import { Toggle } from '../ui/Toggle'
import styles from './TemplateEditor.module.css'

interface ContractIssue {
  code: string
  severity: 'error' | 'warning'
  field?: string
  message: string
}

interface ValidationResult {
  errors: ContractIssue[]
  warnings: ContractIssue[]
}

const CATEGORY_OPTIONS = [
  { value: 'CLASSIC', label: '클래식' },
  { value: 'MODERN', label: '모던' },
  { value: 'VINTAGE', label: '빈티지' },
  { value: 'FLORAL', label: '플로럴' },
  { value: 'MINIMAL', label: '미니멀' },
  { value: 'ELEGANT', label: '엘레건트' },
  { value: 'NATURAL', label: '내추럴' },
]

function toSlug(name: string): string {
  return name.toLowerCase()
    .replace(/[^a-z0-9가-힣\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 64)
}

interface TemplateDetail {
  id: string
  name: string
  slug: string
  category: string
  tier: string
  isActive: boolean
  sharedHtml: string | null
  sharedCss: string | null
  sharedJs: string | null
  cachedHtml: string | null
  cachedCss: string | null
  cachedJs: string | null
  liquidHtml: string | null
  config: Record<string, unknown>
  usesComposition?: boolean
}

interface Conversion {
  id: string
  liquidHtml: string
  referenceId: string | null
  model: string
  inputTokens: number
  outputTokens: number
  detectedSections: Array<{ sectionType: string; confidence: string; bindings: string[] }>
  notes: string[]
  createdAt: string
}

type EditorTab = 'original' | 'liquid'

export function TemplateEditor() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { addToast } = useToast()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const liquidIframeRef = useRef<HTMLIFrameElement>(null)
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)

  const { data, isLoading, refetch } = useApiFetch<TemplateDetail>(
    id ? `/api/admin/templates/${id}` : null,
  )

  // 탭별 Monaco 모델 정리 — 언마운트 시 라이브러리가 dispose 하는 건 그때 붙어있던
  // 모델뿐이라, 비활성 탭 모델이 남아 다음 방문에서 낡은 내용으로 되살아나는 것을 막는다.
  // (early return 이 아래에 있어 훅은 여기서 무조건 호출해야 한다)
  useMonacoModelCleanup(`template/${id ?? 'new'}/`)

  // 에디터 상태
  const [activeTab, setActiveTab] = useState<EditorTab>('original')
  const [originalContent, setOriginalContent] = useState('')
  const [liquidContent, setLiquidContent] = useState('')
  const [isDirty, setIsDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  // 이력
  const { data: historyData, refetch: refetchHistory } = useApiFetch<{ conversions: Conversion[] }>(
    id ? `/api/admin/templates/${id}/conversions` : null,
  )
  const conversions = historyData?.conversions ?? []
  const [showHistory, setShowHistory] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // 이름 인라인 수정
  const [isEditingName, setIsEditingName] = useState(false)
  const [editingName, setEditingName] = useState('')

  const handleNameSave = useCallback(async () => {
    setIsEditingName(false)
    if (!id || !editingName.trim() || editingName.trim() === data?.name) return
    const newName = editingName.trim()
    try {
      await apiFetch(`/api/admin/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: newName, slug: toSlug(newName) }),
      })
      addToast({ type: 'success', message: '이름이 변경되었습니다' })
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '이름 변경 실패' })
    }
  }, [id, editingName, data, addToast, refetch])

  const handleCategoryChange = useCallback(async (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!id) return
    const category = e.target.value
    try {
      await apiFetch(`/api/admin/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ category }),
      })
      addToast({ type: 'success', message: '카테고리 변경됨' })
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '카테고리 변경 실패' })
    }
  }, [id, addToast, refetch])

  // Liquid 미리보기 상태
  const [liquidPreviewHtml, setLiquidPreviewHtml] = useState('')
  const [liquidPreviewLoading, setLiquidPreviewLoading] = useState(false)
  const [liquidPreviewError, setLiquidPreviewError] = useState<string | null>(null)

  // 데이터 로드 시 초기화
  useEffect(() => {
    if (!data) return
    setOriginalContent(data.cachedHtml ?? data.sharedHtml ?? '')
    setLiquidContent(data.liquidHtml ?? '')
    setIsDirty(false)
  }, [data])

  // Liquid 미리보기 디바운스 렌더링 (JS는 </body> 앞에 삽입)
  useEffect(() => {
    if (!liquidContent) {
      setLiquidPreviewHtml('')
      setLiquidPreviewError(null)
      return
    }
    setLiquidPreviewLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res = await apiFetch<{ renderedHtml: string }>(
          '/api/admin/templates/render-liquid-preview',
          {
            method: 'POST',
            body: JSON.stringify({ liquidHtml: liquidContent }),
          },
        )
        setLiquidPreviewHtml(res.renderedHtml)
        setLiquidPreviewError(null)
      } catch (err: any) {
        setLiquidPreviewError(err.message || 'Liquid 렌더링 실패')
        setLiquidPreviewHtml('')
      } finally {
        setLiquidPreviewLoading(false)
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [liquidContent])

  const previewHtml = data?.cachedHtml ?? data?.sharedHtml ?? ''

  const handleSave = useCallback(async () => {
    if (!id || !isDirty) return
    setSaving(true)
    try {
      const updates: Record<string, string> = {}
      if (activeTab === 'original') {
        updates.cachedHtml = originalContent
      } else {
        updates.liquidHtml = liquidContent
      }
      await apiFetch(`/api/admin/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })
      addToast({ type: 'success', message: '저장 완료' })
      setIsDirty(false)
    } catch {
      addToast({ type: 'error', message: '저장 실패' })
    } finally {
      setSaving(false)
    }
  }, [id, activeTab, originalContent, liquidContent, isDirty, addToast])

  const handleFormat = useCallback(() => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run()
  }, [])

  // 14개 표준 컨트롤러 <script> 를 현재 liquidHtml 에 멱등 주입 → 에디터에 반영.
  // 이미 sentinel 있는 컨트롤러는 skip. 저장 시 liquidHtml 컬럼에 그대로 persist 되어
  // 렌더 시점에도 추가 주입 없이 사용된다 (injectControllers 멱등).
  const handleInjectControllers = useCallback(async () => {
    if (!liquidContent) {
      addToast({ type: 'error', message: 'Liquid HTML 이 비어있습니다' })
      return
    }
    try {
      const res = await apiFetch<{ html: string; injected: string[] }>(
        '/api/admin/templates/inject-controllers',
        { method: 'POST', body: JSON.stringify({ html: liquidContent }) },
      )
      setLiquidContent(res.html)
      setActiveTab('liquid')
      setIsDirty(true)
      const label = res.injected.length > 0 ? `${res.injected.length}개 (${res.injected.join(', ')})` : '신규 주입 없음'
      addToast({ type: 'success', message: `컨트롤러 주입 완료 — ${label}` })
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '주입 실패' })
    }
  }, [liquidContent, addToast])

  // data-fw-*-controller sentinel 붙은 <script> 블록만 제거 (원본 raw shell 로 복원).
  // 사용자가 편집한 스크립트도 sentinel 가 있으면 함께 제거되므로 주의.
  const handleStripControllers = useCallback(() => {
    if (!liquidContent) return
    const re = /<script\b[^>]*data-fw-[a-z-]*-controller[^>]*>[\s\S]*?<\/script>\s*/gi
    const stripped = liquidContent.replace(re, '')
    if (stripped === liquidContent) {
      addToast({ type: 'info', message: '제거할 컨트롤러 스크립트 없음' })
      return
    }
    setLiquidContent(stripped)
    setActiveTab('liquid')
    setIsDirty(true)
    addToast({ type: 'success', message: '컨트롤러 스크립트 제거' })
  }, [liquidContent, addToast])

  const handleStepwiseReanalyze = useCallback(async () => {
    if (!data || !id) return
    try {
      // 최신 변환 이력에서 sourceZipS3Key 확보 (없으면 undefined — extract/upload 재실행 불가).
      let zipS3Key: string | undefined
      try {
        const imports = await apiFetch<{ imports: Array<{ sourceZipS3Key: string | null }> }>(
          `/api/admin/templates/${id}/imports`
        )
        zipS3Key = imports.imports[0]?.sourceZipS3Key ?? undefined
      } catch {
        // 이력 조회 실패는 무시 — 단계별 진입 자체는 계속.
      }

      const initial = emptyStepwiseState()
      initial.html = data.cachedHtml ?? data.sharedHtml ?? ''
      initial.stages = [
        { stage: 'extract', ok: true, info: { restoredFromTemplate: true } },
        { stage: 'upload', ok: true, info: { restoredFromTemplate: true } },
      ]
      initial.artifacts = {
        sharedHtml: data.sharedHtml ?? '',
        sharedCss: data.sharedCss ?? null,
        sharedJs: data.sharedJs ?? null,
        cachedHtml: data.cachedHtml ?? data.sharedHtml ?? '',
        cachedCss: data.cachedCss ?? null,
        uploadedAssets: [],
      }

      const session: StepwiseSession = {
        sessionId: crypto.randomUUID(),
        zipS3Key,
        templateId: id,
        name: data.name,
        slug: data.slug,
        skipLlm: true,
        initialState: initial,
        initialStage: 'scan',
      }
      navigate('/wedding/templates/import/stepwise', { state: session })
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err)
      addToast({ type: 'error', message: `단계별 진입 실패: ${m}` })
    }
  }, [data, id, navigate, addToast])

  const handleApplyConversion = useCallback(async (conversionId: string) => {
    if (!id) return
    try {
      await apiFetch<{ applied: string; validation?: ValidationResult }>(
        `/api/admin/templates/${id}/apply-conversion`,
        {
          method: 'POST',
          body: JSON.stringify({ conversionId }),
        },
      )
      addToast({ type: 'success', message: '변환 결과가 적용되었습니다' })
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '적용 실패' })
    }
  }, [id, addToast, refetch])

  const handleDelete = useCallback(async () => {
    if (!id) return
    setDeleting(true)
    try {
      await apiFetch(`/api/admin/templates/${id}`, { method: 'DELETE' })
      addToast({ type: 'success', message: '템플릿이 삭제되었습니다' })
      navigate('/wedding/templates')
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '삭제 실패' })
    } finally {
      setDeleting(false)
    }
  }, [id, addToast, navigate])

  const handleToggleActive = useCallback(async () => {
    if (!id || !data) return
    try {
      await apiFetch(`/api/admin/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !data.isActive }),
      })
      addToast({ type: 'success', message: data.isActive ? '비활성화됨' : '퍼블리시됨' })
      refetch()
    } catch {
      addToast({ type: 'error', message: '상태 변경 실패' })
    }
  }, [id, data, addToast, refetch])

  // 커버 이미지 편집 가능 여부 토글 — config.options.coverImageEditable에 저장.
  // 서버가 config를 부분 병합하지 않으므로 기존 config를 통째로 spread해서 전송한다.
  const handleToggleCoverEditable = useCallback(async () => {
    if (!id || !data) return
    const prevOptions = (data.config?.options ?? {}) as Record<string, unknown>
    const next = !((prevOptions.coverImageEditable as boolean | undefined) ?? true)
    try {
      await apiFetch(`/api/admin/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          config: { ...data.config, options: { ...prevOptions, coverImageEditable: next } },
        }),
      })
      addToast({ type: 'success', message: next ? '커버 이미지 편집 허용됨' : '커버 이미지 편집 숨김' })
      refetch()
    } catch {
      addToast({ type: 'error', message: '커버 이미지 편집 설정 변경 실패' })
    }
  }, [id, data, addToast, refetch])

  if (isLoading) {
    return <div className={styles.loading}><Spinner /></div>
  }

  if (!data) {
    return (
      <div className={styles.notFound}>
        <p>템플릿을 찾을 수 없습니다</p>
        <Button variant="outline" onClick={() => navigate('/wedding/templates')}>목록으로</Button>
      </div>
    )
  }

  const editorValue = activeTab === 'original' ? originalContent : liquidContent
  // Liquid 탭은 'liquid' 모드(basic-language, 진단 없음)로 봐야 {% %}·{{ }} 가 오류로 안 잡힌다.
  const editorLanguage = activeTab === 'liquid' ? 'liquid' : 'html'
  // 탭마다 다른 path(=모델 URI). path 가 없으면 두 탭이 모델을 공유해 언어 전환 시
  // 이전 언어의 비동기 검증 결과가 현재 내용에 허위 마커로 남는다.
  const editorPath = `template/${id ?? 'new'}/${activeTab}.${activeTab === 'liquid' ? 'liquid' : 'html'}`

  return (
    <div className={styles.page}>
      {/* 상단 툴바 */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <Button variant="ghost" size="sm" onClick={() => navigate('/wedding/templates')}>
            ← 목록
          </Button>
          {isEditingName ? (
            <input
              className={styles.templateNameInput}
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNameSave()
                if (e.key === 'Escape') setIsEditingName(false)
              }}
              autoFocus
            />
          ) : (
            <span
              className={styles.templateName}
              onClick={() => { setIsEditingName(true); setEditingName(data.name) }}
              title="클릭하여 이름 변경"
            >
              {data.name}
              <span className={styles.editIcon}>&#9998;</span>
            </span>
          )}
          <select
            className={styles.categorySelect}
            value={data.category}
            onChange={handleCategoryChange}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <Badge color={data.isActive ? 'green' : 'yellow'}>
            {data.isActive ? '활성' : '비활성'}
          </Badge>
          {data.liquidHtml && <Badge color="blue">Liquid</Badge>}
          {isDirty && <Badge color="red">수정됨</Badge>}
        </div>
        <div className={styles.toolbarRight}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
          >
            이력 {conversions.length > 0 && `(${conversions.length})`}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleFormat}>
            Format
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleInjectControllers}
            disabled={!liquidContent}
            title="Liquid HTML 에 14개 표준 컨트롤러 <script> 멱등 주입 (이미 있으면 skip)"
          >
            컨트롤러 주입
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleStripControllers}
            disabled={!liquidContent}
            title="data-fw-*-controller sentinel 붙은 <script> 블록 모두 제거"
          >
            컨트롤러 제거
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleStepwiseReanalyze}
            title="파이프라인 scan 단계부터 수동 검증 — extract/upload 는 현재 저장본에서 복원"
          >
            단계별 재변환
          </Button>
          <Toggle
            checked={
              ((data.config?.options as Record<string, unknown> | undefined)
                ?.coverImageEditable as boolean | undefined) ?? true
            }
            onChange={handleToggleCoverEditable}
            label="커버 이미지 편집"
            size="sm"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || saving}
          >
            {saving ? '저장 중...' : '저장'}
          </Button>
          <Button
            variant={data.isActive ? 'outline' : 'primary'}
            size="sm"
            onClick={handleToggleActive}
          >
            {data.isActive ? '비활성화' : '퍼블리시'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDelete(true)}
            className={styles.deleteBtn}
          >
            삭제
          </Button>
        </div>
      </div>

      {/* 에디터 탭 */}
      <Tabs
        className={styles.tabBar}
        size="sm"
        items={[
          { key: 'original', label: '원본 HTML' },
          { key: 'liquid', label: liquidContent ? 'Liquid HTML' : 'Liquid HTML (없음)' },
        ]}
        value={activeTab}
        onChange={(key) => setActiveTab(key as EditorTab)}
      />

      {/* 메인 콘텐츠 */}
      <div className={styles.main}>
        <div className={styles.editorPane}>
          {activeTab === 'liquid' && data.usesComposition && (
            <Alert variant="warning">
              이 템플릿은 조립 모델입니다. 여기서 Liquid를 직접 수정하면 다음 조립 저장 때 덮어써집니다.
              섹션 변경은 <a href={`/wedding/templates/${id}/compose`}>조립 페이지</a>에서 하세요.
            </Alert>
          )}
          <Editor
            height="100%"
            path={editorPath}
            defaultLanguage="html"
            language={editorLanguage}
            value={editorValue}
            onChange={(v) => {
              if (activeTab === 'original') setOriginalContent(v ?? '')
              else setLiquidContent(v ?? '')
              setIsDirty(true)
            }}
            onMount={(editor) => { editorRef.current = editor }}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 13,
              wordWrap: 'on',
              tabSize: 2,
            }}
          />
        </div>
        <div className={liquidContent ? styles.previewPaneDual : styles.previewPane}>
          {/* 원본 HTML 미리보기 */}
          <div className={styles.previewColumn}>
            <div className={styles.previewLabel}>원본 미리보기</div>
            <div className={styles.previewFrame}>
              {previewHtml ? (
                <iframe
                  ref={iframeRef}
                  srcDoc={previewHtml}
                  title="원본 미리보기"
                  className={styles.previewIframe}
                />
              ) : (
                <div className={styles.previewLoading}>미리보기 없음</div>
              )}
            </div>
          </div>

          {/* Liquid 미리보기 (샘플 데이터) */}
          {liquidContent && (
            <div className={styles.previewColumn}>
              <div className={styles.previewLabel}>Liquid 미리보기 (샘플 데이터)</div>
              <div className={styles.previewFrame}>
                {liquidPreviewLoading ? (
                  <div className={styles.previewLoading}><Spinner /> 렌더링 중...</div>
                ) : liquidPreviewError ? (
                  <div className={styles.previewError}>
                    <div className={styles.previewErrorTitle}>Liquid 렌더링 오류</div>
                    <pre className={styles.previewErrorPre}>{liquidPreviewError}</pre>
                  </div>
                ) : liquidPreviewHtml ? (
                  <iframe
                    ref={liquidIframeRef}
                    srcDoc={liquidPreviewHtml}
                    title="Liquid 미리보기"
                    className={styles.previewIframe}
                  />
                ) : (
                  <div className={styles.previewLoading}>Liquid 미리보기 없음</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 변환 이력 Drawer */}
      <Drawer
        open={showHistory}
        onClose={() => setShowHistory(false)}
        title={`변환 이력${conversions.length > 0 ? ` (${conversions.length})` : ''}`}
        size="md"
      >
        {conversions.length === 0 ? (
          <div className={styles.historyEmpty}>변환 이력이 없습니다</div>
        ) : (
          <div className={styles.historyList}>
            {[...conversions].reverse().map((conv) => (
              <div key={conv.id} className={styles.historyItem}>
                <div className={styles.historyMeta}>
                  <span className={styles.historyDate}>
                    {new Date(conv.createdAt).toLocaleString('ko-KR')}
                  </span>
                  <Badge color="blue">{conv.model.split('/').pop()?.split('-').slice(0, 2).join('-') ?? conv.model}</Badge>
                  <span className={styles.historyTokens}>
                    {conv.inputTokens + conv.outputTokens} tokens
                  </span>
                </div>
                <div className={styles.historySections}>
                  {conv.detectedSections?.map((s) => (
                    <Badge key={s.sectionType} color={s.confidence === 'high' ? 'green' : 'yellow'}>
                      {s.sectionType}
                    </Badge>
                  ))}
                </div>
                {conv.notes && conv.notes.length > 0 && (
                  <div className={styles.historyNotes}>
                    {conv.notes.map((n, i) => <p key={i}>{n}</p>)}
                  </div>
                )}
                <div className={styles.historyActions}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setLiquidContent(conv.liquidHtml); setActiveTab('liquid'); setIsDirty(true); setShowHistory(false) }}
                  >
                    에디터에 로드
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleApplyConversion(conv.id)}
                  >
                    이 버전 적용
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="템플릿 삭제"
        message={`"${data.name}" 템플릿을 삭제하시겠습니까? 변환 이력은 보존됩니다.`}
        confirmLabel="삭제"
        variant="danger"
        loading={deleting}
      />
    </div>
  )
}
